import { QUIKLEARN_V2_BACKEND_URL } from 'config/app.config';
import mockLesson720L from './mockData/lesson-720L.json';

const TOKEN_STORAGE_KEY = 'quiklearn_lcv_auth_token';

const MOCK_DATA = {
  [mockLesson720L?.metadata?.lessonId || 'G3-MATH-L013-720L']: mockLesson720L,
};

export function isMockMode() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('useMock') === 'true';
}

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // silent
  }
}

/**
 * Resolve auth token: URL param takes priority, then localStorage.
 * A new param always overwrites the stored value.
 */
export function resolveToken() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const paramToken = params.get('token');
  if (paramToken) {
    storeToken(paramToken);
    return paramToken;
  }
  return getStoredToken();
}

const getBaseUrl = () => String(QUIKLEARN_V2_BACKEND_URL || '').replace(/\/$/, '');

/**
 * Event bus for v2 backend auth-expired (401) responses. Anything calling the
 * v2 backend (heartbeats, key-term writes, lesson-progress reads/writes,
 * lesson content, next-lesson lookup) notifies subscribers so the UI can
 * prompt the learner to re-authenticate.
 */
const authExpiredListeners = new Set();

export function onV2AuthExpired(listener) {
  if (typeof listener !== 'function') return () => {};
  authExpiredListeners.add(listener);
  return () => {
    authExpiredListeners.delete(listener);
  };
}

function emitV2AuthExpired() {
  for (const listener of authExpiredListeners) {
    try {
      listener();
    } catch {
      // listener should never throw back into the fetch path
    }
  }
}

export async function fetchLessonContent(lessonId, token) {
  if (isMockMode()) {
    const mock = MOCK_DATA[lessonId];
    if (!mock) {
      throw new Error(`No mock data for lessonId: ${lessonId}`);
    }
    console.log('[LessonContentService] Using mock data for', lessonId);
    return { content: mock };
  }

  const base = getBaseUrl();
  const url = `${base}/lesson-content/${lessonId}?includeContent=true`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 401) {
      emitV2AuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (status === 403) {
      throw new Error('You do not have permission to view this lesson.');
    }
    if (status === 404) {
      throw new Error(`Lesson not found: ${lessonId}`);
    }
    throw new Error(`Failed to fetch lesson content (${status})`);
  }

  const json = await response.json();
  return json.data;
}

async function authorizedRequest(
  url,
  {method = 'GET', token, body, keepalive = false} = {},
) {
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    keepalive: Boolean(keepalive),
  });

  if (!response.ok) {
    if (response.status === 401) {
      emitV2AuthExpired();
    }
    throw new Error(`${method} ${url} failed (${response.status})`);
  }

  const text = await response.text();
  if (!text) return null;
  try {
    const json = JSON.parse(text);
    return json?.data !== undefined ? json.data : json;
  } catch {
    return null;
  }
}

export async function postLessonHeartbeat(
  {lessonId, term, termStatus, durationSeconds},
  token,
  {keepalive = false} = {},
) {
  if (!lessonId || !token) return null;
  if (isMockMode()) return null;

  const body = {lessonId, durationSeconds: Math.max(0, durationSeconds | 0)};
  if (term && termStatus) {
    body.term = term;
    body.termStatus = termStatus;
  }

  try {
    return await authorizedRequest(`${getBaseUrl()}/lesson-activity`, {
      method: 'POST',
      token,
      body,
      keepalive,
    });
  } catch (error) {
    console.warn('[LessonContentService] heartbeat failed:', error?.message || error);
    return null;
  }
}

/**
 * GET /key-term-progress?lessonId=... — list the current user's per-key-term
 * progress rows for a lesson. Used at lesson load to pre-populate the
 * progress tracker so terms the learner has already worked through show as
 * checked even when the resume flow drops them back to an earlier term.
 *
 * Returns an array of `{term, status, questionsAttempted, questionsCorrect, ...}`
 * rows or an empty array. Returns `[]` (not null) on transport errors so
 * callers don't have to null-check.
 */
export async function getKeyTermProgress(lessonId, token) {
  if (!lessonId || !token) return [];
  if (isMockMode()) return [];

  try {
    const url = `${getBaseUrl()}/key-term-progress?lessonId=${encodeURIComponent(
      lessonId,
    )}`;
    const data = await authorizedRequest(url, {method: 'GET', token});
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.warn(
      '[LessonContentService] get key-term-progress failed:',
      error?.message || error,
    );
    return [];
  }
}

export async function putKeyTermProgress(
  {lessonId, term, questionsAttempted, questionsCorrect, status},
  token,
) {
  if (!lessonId || !term || !token) return null;
  if (isMockMode()) return null;

  try {
    return await authorizedRequest(`${getBaseUrl()}/key-term-progress`, {
      method: 'PUT',
      token,
      body: {
        lessonId,
        term,
        questionsAttempted: Math.max(0, questionsAttempted | 0),
        questionsCorrect: Math.max(0, questionsCorrect | 0),
        status,
      },
    });
  } catch (error) {
    console.warn(
      '[LessonContentService] key-term-progress failed:',
      error?.message || error,
    );
    return null;
  }
}

export async function putLessonProgress(
  lessonId,
  {questionsAttempted, questionsCorrect},
  token,
) {
  if (!lessonId || !token) return null;
  if (isMockMode()) return null;

  try {
    return await authorizedRequest(
      `${getBaseUrl()}/lesson-progress/${encodeURIComponent(lessonId)}`,
      {
        method: 'PUT',
        token,
        body: {
          questionsAttempted: Math.max(0, questionsAttempted | 0),
          questionsCorrect: Math.max(0, questionsCorrect | 0),
        },
      },
    );
  } catch (error) {
    console.warn(
      '[LessonContentService] lesson-progress failed:',
      error?.message || error,
    );
    return null;
  }
}

export async function getCurrentLessonStatus(token) {
  if (!token) return null;
  if (isMockMode()) return null;

  try {
    return await authorizedRequest(`${getBaseUrl()}/current-lesson-status`, {
      method: 'GET',
      token,
    });
  } catch (error) {
    console.warn(
      '[LessonContentService] current-lesson-status failed:',
      error?.message || error,
    );
    return null;
  }
}

export async function getLessonProgress(lessonId, token) {
  if (!lessonId || !token) return null;
  if (isMockMode()) return null;

  try {
    return await authorizedRequest(
      `${getBaseUrl()}/lesson-progress/${encodeURIComponent(lessonId)}`,
      {
        method: 'GET',
        token,
      },
    );
  } catch (error) {
    console.warn(
      '[LessonContentService] get lesson-progress failed:',
      error?.message || error,
    );
    return null;
  }
}

/**
 * Fetch the pre-rendered audio manifest for a lesson. The response shape
 * is documented in the backend's FRONTEND_LESSON_AUDIO_FLOW.md — every
 * clip is keyed by its logical `audioId` and carries `url`,
 * `urlExpiresAt`, `durationMs`, `fileSize`, `mimeType`, and
 * `timingMetadata.words[]` for read-along highlighting.
 *
 * Returns `null` (instead of throwing) when no manifest exists yet for
 * the lesson — that case is non-fatal and the viewer falls back to the
 * per-clip download endpoint (with `?autoGenerate=true`) on first use.
 */
export async function fetchLessonAudioManifest(lessonId, token) {
  if (!lessonId || !token) return null;
  if (isMockMode()) return null;

  const base = getBaseUrl();
  const url = `${base}/lesson-audio/manifest/${encodeURIComponent(lessonId)}`;

  const response = await fetch(url, {
    headers: {Authorization: `Bearer ${token}`},
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      emitV2AuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(`Failed to fetch lesson audio manifest (${response.status})`);
  }

  const json = await response.json();
  return json?.data ?? json ?? null;
}

/**
 * Fetch metadata for a single audio clip. Pass `autoGenerate: true` to
 * trigger backend on-demand rendering when the row is missing — useful
 * as a per-clip fallback when the manifest doesn't include the clip yet.
 *
 * Returns the audio record (with a fresh presigned `url`) or `null` on
 * 404. Throws on other errors so the caller can surface them.
 */
export async function fetchLessonAudioRecord(
  audioId,
  lessonContentId,
  token,
  {autoGenerate = false} = {},
) {
  if (!audioId || !token) return null;
  if (isMockMode()) return null;

  const base = getBaseUrl();
  const params = new URLSearchParams();
  if (lessonContentId) params.set('lessonContentId', lessonContentId);
  if (autoGenerate) params.set('autoGenerate', 'true');
  const qs = params.toString();
  const url = `${base}/lesson-audio/${encodeURIComponent(audioId)}${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, {
    headers: {Authorization: `Bearer ${token}`},
  });

  if (response.status === 404) return null;

  if (!response.ok) {
    if (response.status === 401) {
      emitV2AuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(`Failed to fetch lesson audio (${response.status})`);
  }

  const json = await response.json();
  return json?.data ?? json ?? null;
}

/**
 * Stream a single audio clip's binary from the backend `/download`
 * endpoint and return it as a Blob. This is the primary fallback used
 * during playback when the in-memory manifest entry is missing or its
 * presigned URL has expired (per FRONTEND_LESSON_AUDIO_FLOW.md).
 */
export async function downloadLessonAudioBlob(audioId, lessonContentId, token) {
  if (!audioId || !token) return null;
  if (isMockMode()) return null;

  const base = getBaseUrl();
  const params = new URLSearchParams();
  if (lessonContentId) params.set('lessonContentId', lessonContentId);
  const qs = params.toString();
  const url = `${base}/lesson-audio/${encodeURIComponent(audioId)}/download${
    qs ? `?${qs}` : ''
  }`;

  const response = await fetch(url, {
    headers: {Authorization: `Bearer ${token}`},
  });

  if (response.status === 404) return null;

  if (!response.ok) {
    if (response.status === 401) {
      emitV2AuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error(`Failed to download lesson audio (${response.status})`);
  }

  return response.blob();
}

export async function fetchNextLesson(lessonId, token) {
  if (isMockMode()) {
    return {
      lessonId: 'no more lessons',
      lessonLink: null,
    };
  }

  const base = getBaseUrl();
  const url = `${base}/next-lesson-id`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ lessonId }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 401) {
      emitV2AuthExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (status === 403) {
      throw new Error('You do not have permission to access the next lesson.');
    }
    if (status === 404) {
      throw new Error('Unable to find the next lesson.');
    }
    throw new Error(`Failed to fetch next lesson (${status})`);
  }

  const json = await response.json();
  return json?.data || json;
}
